'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { Slot } from '../slot';
import { ToggleButtonGroupContext } from './context/toggle-button-group.context';
import { toggleButtonGroupRecipe } from './styles/toggle-button-group.styles';
import type { ToggleButtonGroupPropsInterface } from './types/toggle-button-group.types';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['ToggleButton', 'LoadingButton', 'TextButton', 'Button']
 * @since 8.0.0
 */

export const ToggleButtonGroup = forwardRef<HTMLDivElement, ToggleButtonGroupPropsInterface>(
  (props: ToggleButtonGroupPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      conjoined = true,
      children,
      className,
      css: cssProp,
      value,
      defaultValue,
      onValueChange,
      size = 'sm',
      disabled,
      ...rest
    } = props;

    const isValueControlled = 'value' in props;

    const [valueState, setValueState] = useControllableValue<string | Array<string> | undefined>({
      controlledValue: value,
      uncontrolledValue: defaultValue,
      isControlled: isValueControlled,
      isUncontrolled: 'defaultValue' in props,
      onChange: (newValue) => {
        if (newValue !== undefined) {
          onValueChange?.(newValue);
        }
      },
      componentName: 'ToggleButtonGroup',
      controlledPropName: 'value',
      uncontrolledPropName: 'defaultValue',
    });

    const styles = css(
      toggleButtonGroupRecipe.raw({
        conjoined,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(
      () => ({ size, value: valueState, setValue: setValueState, disabled }),
      [size, valueState, setValueState, disabled],
    );

    return (
      <ToggleButtonGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ToggleButtonGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </Component>
      </ToggleButtonGroupContext.Provider>
    );
  },
);

ToggleButtonGroup.displayName = 'ToggleButtonGroup';
